import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ExplanationDisplayType } from '@shared/enums/explanation-display-type';
import { QuestionType } from '@shared/enums/QuestionType';
import {
    IReportTemplate,
    IReportTemplateRowType,
    IResponsible,
    ITemplateAreaForm,
    ITemplateForm,
    ITemplateRowForm,
    ITemplateSectionForm,
    ITemplateSectionGuidanceForm
} from '@shared/interfaces';
import * as uuid from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class TemplateBuilderService {
    formGroup: FormGroup<ITemplateForm>;
    activeSection = 0;
    currentEditingLang = 'en';

    constructor(private fb: FormBuilder) {}

    public setUpTemplate(organisation: IResponsible) {
        this.formGroup = this.fb.group<ITemplateForm>({
            id: new FormControl(''),
            title: new FormControl(''),
            description: new FormControl(''),
            orgCode: new FormControl(organisation?.orgCode),
            responsibleType: new FormControl(organisation?.responsibleType),
            sections: this.fb.array([] as FormGroup<ITemplateSectionForm>[])
        });
        this.addSection();
    }

    public removeSection(index: number): void {
        this.formGroup.controls.sections.removeAt(index);
    }

    public addSection(): void {
        this.formGroup.controls.sections.push(
            this.fb.group<ITemplateSectionForm>({
                title: new FormControl('', Validators.required),
                titleDe: new FormControl(''),
                description: new FormControl(''),
                descriptionDe: new FormControl(''),
                areas: this.fb.array([
                    this.fb.group<ITemplateAreaForm>({
                        description: new FormControl('', Validators.required),
                        descriptionDe: new FormControl(''),
                        guidanceId: new FormControl(''),
                        guidanceText: new FormControl(''),
                        guidanceTextDe: new FormControl(''),
                        questionType: new FormControl(QuestionType.text, Validators.required),
                        rows: this.fb.array([
                            this.fb.array([
                                this.fb.group<ITemplateRowForm>({
                                    title: new FormControl(''),
                                    titleDe: new FormControl(''),
                                    type: new FormControl(IReportTemplateRowType.textarea, Validators.required),
                                    valueId: new FormControl(uuid.v4().replace('-', ''))
                                })
                            ])
                        ]),
                        dependsOn: new FormControl(-1, Validators.required),
                        dependsOnAnswer: new FormControl(null)
                    })
                ]),
                guidance: this.fb.group<ITemplateSectionGuidanceForm>({
                    id: new FormControl(uuid.v4().replace('-', ''), Validators.required),
                    content: new FormControl('', Validators.required),
                    contentDe: new FormControl('')
                })
            })
        );
    }

    public addQuestion(sectionIndex: number, questionType: QuestionType): void {
        const section = this.formGroup.controls.sections.at(sectionIndex);
        const areas = section.controls.areas;

        let questionGroup: FormGroup<ITemplateAreaForm>;
        switch (questionType) {
            case QuestionType.text:
                questionGroup = this.fb.group<ITemplateAreaForm>({
                    description: new FormControl('', Validators.required),
                    descriptionDe: new FormControl(''),
                    guidanceId: new FormControl(''),
                    guidanceText: new FormControl(''),
                    guidanceTextDe: new FormControl(''),
                    questionType: new FormControl(questionType, Validators.required),
                    rows: this.fb.array([
                        this.fb.array([
                            this.fb.group<ITemplateRowForm>({
                                title: new FormControl(''),
                                titleDe: new FormControl(''),
                                type: new FormControl(IReportTemplateRowType.textarea, Validators.required),
                                valueId: new FormControl(uuid.v4().replace('-', ''))
                            })
                        ])
                    ]),
                    dependsOn: new FormControl(-1, Validators.required),
                    dependsOnAnswer: new FormControl(null)
                });
                break;

            case QuestionType.yesNo:
                questionGroup = this.fb.group<ITemplateAreaForm>({
                    description: new FormControl('', Validators.required),
                    descriptionDe: new FormControl(''),
                    guidanceId: new FormControl(''),
                    guidanceText: new FormControl(''),
                    guidanceTextDe: new FormControl(''),
                    questionType: new FormControl(questionType, Validators.required),
                    isExplanationDisplayed: new FormControl(false),
                    isExplanationMandatory: new FormControl(false),
                    explanationDisplayType: new FormControl(ExplanationDisplayType.both),
                    rows: this.fb.array([
                        this.fb.array([
                            this.fb.group<ITemplateRowForm>({
                                title: new FormControl(''),
                                titleDe: new FormControl(''),
                                type: new FormControl(IReportTemplateRowType.checkbox, Validators.required),
                                valueId: new FormControl(uuid.v4().replace('-', ''))
                            }),
                            this.fb.group<ITemplateRowForm>({
                                title: new FormControl('Explanation'),
                                titleDe: new FormControl('Erklärung'),
                                type: new FormControl(IReportTemplateRowType.textarea, Validators.required),
                                valueId: new FormControl(uuid.v4().replace('-', ''))
                            })
                        ])
                    ]),
                    dependsOn: new FormControl(-1, Validators.required),
                    dependsOnAnswer: new FormControl(null)
                });
                break;

            case QuestionType.value:
                questionGroup = this.fb.group<ITemplateAreaForm>({
                    description: new FormControl('', Validators.required),
                    descriptionDe: new FormControl(''),
                    guidanceId: new FormControl(''),
                    guidanceText: new FormControl(''),
                    guidanceTextDe: new FormControl(''),
                    questionType: new FormControl(questionType, Validators.required),
                    rows: this.fb.array([
                        this.fb.array([
                            this.fb.group<ITemplateRowForm>({
                                title: new FormControl(''),
                                titleDe: new FormControl(''),
                                type: new FormControl(IReportTemplateRowType.input, Validators.required),
                                valueId: new FormControl(uuid.v4().replace('-', ''))
                            })
                        ])
                    ]),
                    dependsOn: new FormControl(-1, Validators.required),
                    dependsOnAnswer: new FormControl(null)
                });
                break;

            default:
                throw new Error(`Unsupported question type: ${String(questionType)}`);
        }

        areas.push(questionGroup);
    }

    public removeQuestion(sectionIndex: number, questionIndex: number): void {
        const section = this.formGroup.controls.sections.at(sectionIndex);
        const areas = section.controls.areas;

        if (areas && areas.length > questionIndex) {
            areas.removeAt(questionIndex);
        } else {
            console.error('Question index out of bounds or areas control is undefined');
        }
    }

    public reorder(oldIndex: number, newIndex: number): void {
        const sections = this.formGroup.controls.sections;
        const curSection = sections.at(oldIndex);
        sections.removeAt(oldIndex);
        sections.insert(newIndex, curSection);

        if (this.activeSection === oldIndex) {
            this.activeSection = newIndex;
        } else if (this.activeSection > oldIndex && newIndex >= this.activeSection) {
            this.activeSection -= 1;
        } else if (this.activeSection < oldIndex && newIndex <= this.activeSection) {
            this.activeSection += 1;
        }
    }

    public setActiveEditSection(index: number): void {
        this.activeSection = index;
    }

    public removeFromRow(group: FormGroup<ITemplateRowForm>, index: number, parentIndex: number): void {
        const parent = group.parent as FormArray<FormGroup<ITemplateRowForm>>;
        const parentsParent = parent.parent as FormArray<FormArray<FormGroup<ITemplateRowForm>>>;
        const rootParent = parentsParent.parent as FormGroup<ITemplateAreaForm>;

        if (parent.length > 1) {
            parent.removeAt(index);
        } else if (rootParent.controls.rows.length > 1) {
            parent.removeAt(index);
            if (!parent.length) {
                parentsParent.removeAt(parentIndex);
            }
        }
    }

    public reset(): void {
        this.currentEditingLang = 'en';
        this.formGroup = null;
        this.activeSection = 0;
    }

    public buildTemplate(item: IReportTemplate, organisation: IResponsible, isDuplicate: boolean): void {
        this.formGroup = this.fb.group<ITemplateForm>({
            id: new FormControl(isDuplicate ? null : item.id),
            title: new FormControl(isDuplicate ? null : item.title),
            description: new FormControl(isDuplicate ? null : item.description),
            orgCode: new FormControl(isDuplicate ? organisation?.orgCode : item.orgCode),
            responsibleType: new FormControl(isDuplicate ? null : organisation?.responsibleType),
            sections: this.fb.array(
                item.sections.map(section =>
                    this.fb.group<ITemplateSectionForm>({
                        title: new FormControl(section.title, Validators.required),
                        titleDe: new FormControl(section.titleDe),
                        description: new FormControl(section.description),
                        descriptionDe: new FormControl(section.descriptionDe),
                        areas: this.fb.array(
                            section.areas.map(area =>
                                this.fb.group<ITemplateAreaForm>({
                                    description: new FormControl(area.description, Validators.required),
                                    descriptionDe: new FormControl(area.descriptionDe),
                                    guidanceText: new FormControl(area.guidanceText),
                                    guidanceTextDe: new FormControl(area.guidanceTextDe),
                                    guidanceId: new FormControl(area.guidanceId),
                                    questionType: new FormControl(area.questionType),
                                    isExplanationDisplayed: new FormControl(area.isExplanationDisplayed),
                                    isExplanationMandatory: new FormControl(area.isExplanationMandatory),
                                    explanationDisplayType: new FormControl(
                                        area.explanationDisplayType ?? ExplanationDisplayType.both
                                    ),
                                    rows: this.fb.array(
                                        area.rows.map(rows =>
                                            this.fb.array(
                                                rows.map(row =>
                                                    this.fb.group<ITemplateRowForm>({
                                                        title: new FormControl(row.title),
                                                        titleDe: new FormControl(row.titleDe),
                                                        type: new FormControl(row.type, Validators.required),
                                                        valueId: new FormControl(row.valueId)
                                                    })
                                                )
                                            )
                                        )
                                    ),
                                    dependsOn: new FormControl(area.dependsOn, Validators.required),
                                    dependsOnAnswer: new FormControl(area.dependsOnAnswer)
                                })
                            )
                        ),
                        guidance: this.fb.group<ITemplateSectionGuidanceForm>({
                            id: new FormControl(
                                section.guidance?.id ?? uuid.v4().replace('-', ''),
                                Validators.required
                            ),
                            content: new FormControl(section.guidance?.content ?? '', Validators.required),
                            contentDe: new FormControl(section.guidance?.contentDe ?? '')
                        })
                    })
                )
            )
        });
        this.activeSection = 0;
    }
}
